/**
 * useNotifier - Système de notifications toast réutilisable.
 *
 * API :
 *   const notify = useNotifier()
 *   notify.success(message, { title?, duration?, action? })
 *   notify.error(message, options)
 *   notify.warning(message, options)
 *   notify.info(message, options)
 *   notify.dismiss(id)
 *
 * Le composant <AppToaster /> doit être monté une fois dans App.vue
 * pour afficher la pile de notifications.
 */
import { reactive, computed } from 'vue'

const state = reactive({
  toasts: [],
})

let idCounter = 0

const DEFAULT_DURATION = {
  success: 3500,
  info: 3500,
  warning: 4500,
  error: 5500,
}

function push(type, message, options = {}) {
  const id = ++idCounter
  const toast = {
    id,
    type,
    message,
    title: options.title || null,
    action: options.action || null, // { label, onClick }
    closable: options.closable !== false,
    duration: options.duration ?? DEFAULT_DURATION[type] ?? 4000,
  }
  state.toasts.push(toast)
  if (toast.duration > 0) {
    setTimeout(() => dismiss(id), toast.duration)
  }
  return id
}

function dismiss(id) {
  const idx = state.toasts.findIndex((t) => t.id === id)
  if (idx >= 0) state.toasts.splice(idx, 1)
}

function clear() {
  state.toasts.splice(0, state.toasts.length)
}

const toasts = computed(() => state.toasts)

export function useNotifier() {
  return {
    toasts,
    success: (message, options) => push('success', message, options),
    error: (message, options) => push('error', message, options),
    warning: (message, options) => push('warning', message, options),
    info: (message, options) => push('info', message, options),
    dismiss,
    clear,
  }
}
